import { useEffect, useState, type FormEvent } from "react";
import { Link } from "react-router-dom";
import {
  ContactSubmitError,
  fetchContactInit,
  submitContactForm,
} from "../api/contact";
import { config } from "../config";
import {
  getRecaptchaEnterpriseToken,
  loadRecaptchaEnterprise,
  RECAPTCHA_CONTACT_ACTION,
} from "../lib/recaptcha";
import { useLocale } from "../contexts/LocaleContext";
import { useToast } from "../contexts/ToastContext";

export function ContactPage() {
  const toast = useToast();
  const { t } = useLocale();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [topic, setTopic] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [csrfToken, setCsrfToken] = useState("");
  const [captchaReady, setCaptchaReady] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      try {
        const data = await fetchContactInit();
        if (cancelled) return;
        if (data.user?.name) setName(data.user.name);
        if (data.user?.email) setEmail(data.user.email);
        if (data.csrf_token) setCsrfToken(data.csrf_token);
      } catch {
        /* anonymous is fine */
      }

      if (!config.recaptchaSiteKey) return;

      try {
        await loadRecaptchaEnterprise(config.recaptchaSiteKey);
        if (!cancelled) setCaptchaReady(true);
      } catch (err) {
        console.error("reCAPTCHA Enterprise load:", err);
        if (!cancelled) {
          toast.show(t("contact.captchaLoadFailed"), "danger");
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [toast, t]);

  const onSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!consent) {
      toast.show(t("contact.consentRequired"), "danger");
      return;
    }
    if (!csrfToken) {
      toast.show(t("contact.csrfMissing"), "danger");
      return;
    }
    if (!config.recaptchaSiteKey) {
      toast.show(t("contact.recaptchaMissing"), "danger");
      return;
    }

    setSubmitting(true);
    try {
      const recaptchaToken = await getRecaptchaEnterpriseToken(
        config.recaptchaSiteKey,
        RECAPTCHA_CONTACT_ACTION,
      );

      await submitContactForm({
        name,
        email,
        topic,
        message,
        csrf_token: csrfToken,
        recaptcha_token: recaptchaToken,
      });
      setDone(true);
    } catch (err) {
      if (err instanceof ContactSubmitError) {
        if (err.body.csrf_token) setCsrfToken(err.body.csrf_token);
        toast.show(err.message, "danger");
        return;
      }
      toast.show(t("contact.error", { error: (err as Error).message }), "danger");
    } finally {
      setSubmitting(false);
    }
  };

  const remaining = 300 - message.length;
  const canSubmit = captchaReady && !!csrfToken && !!config.recaptchaSiteKey;

  return (
    <section className="wrapper contact">
      <div className="row">
        <div className="col-full centered">
          <h1>{t("contact.title")}</h1>
          <p className="centered">
            {t("contact.faqHint")}{" "}
            <Link to="/help">{t("contact.faq")}</Link> {t("contact.faqSuffix")}
          </p>

          {!done ? (
            <form
              id="contactForm"
              className="contact-form"
              onSubmit={onSubmit}
              noValidate
            >
              <h2 style={{ margin: 0 }}>{t("contact.formTitle")}</h2>
              <p className="centered" style={{ marginTop: 0 }}>
                <small>{t("contact.requiredHint")}</small>
              </p>

              <div className="form-row">
                <label htmlFor="name">{t("contact.name")}</label>
                <input
                  id="name"
                  name="name"
                  type="text"
                  autoComplete="name"
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>

              <div className="form-row">
                <label htmlFor="email">{t("contact.email")}</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>

              <div className="form-row">
                <label htmlFor="topic">{t("contact.topic")}</label>
                <input
                  id="topic"
                  name="topic"
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                />
              </div>

              <div className="form-row">
                <label htmlFor="message">
                  {t("contact.message")}{" "}
                  <small>{t("contact.maxChars")}</small>
                </label>
                <textarea
                  id="message"
                  name="message"
                  rows={10}
                  maxLength={300}
                  required
                  className="textarea-large"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                />
                <div>
                  <small>
                    {t("contact.charsRemaining")} <span>{remaining}</span>
                  </small>
                </div>
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={consent}
                    onChange={(e) => setConsent(e.target.checked)}
                    required
                  />
                  <span>
                    {t("contact.consent")}{" "}
                    <Link to="/datenschutz">{t("contact.privacy")}</Link>{" "}
                    {t("contact.consentSuffix")}
                  </span>
                </label>
              </div>

              <div className="form-actions">
                <button type="submit" disabled={submitting || !canSubmit}>
                  {submitting ? t("contact.sending") : t("contact.send")}
                </button>
              </div>
            </form>
          ) : (
            <div id="thankYou">
              <h2>{t("contact.thankYou")}</h2>
              <p className="centered">
                {t("contact.responseTime")}{" "}
                <strong>{t("contact.hours48")}</strong>.
              </p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
